import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faStore, faCheck,
    faTruckFast, faCut
} from '@fortawesome/free-solid-svg-icons';
import { useScrollAnimation } from '../../helpers/useScrollAnimation';
import './Products.css';

const ProductSection = ({ id, labelKey, titleKey, descKey, features, icon, visual, alt, sectionRef }) => {
    const { t } = useTranslation();

    return (
        <section id={id} className={`section product-section ${alt ? 'section-alt' : ''}`} ref={sectionRef}>
            <div className="container">
                <div className={`product-grid ${alt ? 'product-grid-reverse' : ''}`}>
                    <div className="product-text fade-left">
                        <span className="section-label">{t(labelKey)}</span>
                        <h2 className="section-title">{t(titleKey)}</h2>
                        <p className="product-desc">{t(descKey)}</p>
                        <ul className="product-features">
                            {features.map((key) => (
                                <li key={key}>
                                    <span className="feature-check">
                                        <FontAwesomeIcon icon={faCheck} />
                                    </span>
                                    <span>{t(key)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="product-visual fade-right">
                        <div className="product-visual-card">
                            <div className="product-visual-icon">
                                <FontAwesomeIcon icon={icon} />
                            </div>
                            <div className="product-visual-lines">
                                {visual}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export const ProductTransport = () => {
    const ref = useScrollAnimation();
    return (
        <ProductSection
            id="transport"
            labelKey="transportLabel"
            titleKey="transportTitle"
            descKey="transportDesc"
            features={['transportFeature1', 'transportFeature2', 'transportFeature3', 'transportFeature4']}
            icon={faTruckFast}
            alt={false}
            sectionRef={ref}
            visual={<TransportVisual />}
        />
    );
};

export const ProductStancane = () => {
    const ref = useScrollAnimation();
    return (
        <ProductSection
            id="stancane"
            labelKey="stancaneLabel"
            titleKey="stancaneTitle"
            descKey="stancaneDesc"
            features={['stancaneFeature1', 'stancaneFeature2', 'stancaneFeature3', 'stancaneFeature4']}
            icon={faCut}
            alt={true}
            sectionRef={ref}
            visual={<StancaneVisual />}
        />
    );
};

export const ProductShelf = () => {
    const ref = useScrollAnimation();
    return (
        <ProductSection
            id="shelf"
            labelKey="shelfLabel"
            titleKey="shelfTitle"
            descKey="shelfDesc"
            features={['shelfFeature1', 'shelfFeature2', 'shelfFeature3', 'shelfFeature4']}
            icon={faStore}
            alt={false}
            sectionRef={ref}
            visual={<ShelfVisual />}
        />
    );
};

const TransportVisual = () => (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="product-svg">
        <rect x="20" y="60" width="160" height="90" rx="3" fill="#C8883A" opacity="0.15" stroke="#B07030" strokeWidth="2"/>
        <rect x="20" y="60" width="160" height="15" rx="0" fill="#B07030" opacity="0.25"/>
        <line x1="100" y1="60" x2="100" y2="150" stroke="#B07030" strokeWidth="1.5" strokeDasharray="4 3"/>
        <path d="M20 60 L60 35 L200 35 L160 60" fill="#E8A54C" opacity="0.2" stroke="#B07030" strokeWidth="1.5"/>
        <path d="M160 60 L200 35 L200 125 L160 150" fill="#C8883A" opacity="0.18" stroke="#B07030" strokeWidth="1.5"/>
        <rect x="50" y="90" width="40" height="28" rx="2" fill="#B07030" opacity="0.2" stroke="#B07030" strokeWidth="1"/>
        <rect x="110" y="90" width="40" height="28" rx="2" fill="#B07030" opacity="0.2" stroke="#B07030" strokeWidth="1"/>
        <line x1="40" y1="150" x2="160" y2="150" stroke="#7A5C44" strokeWidth="1" opacity="0.4"/>
    </svg>
);

const StancaneVisual = () => (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="product-svg">
        <rect x="40" y="30" width="120" height="120" rx="4" fill="#C8883A" opacity="0.12" stroke="#B07030" strokeWidth="2"/>
        <rect x="60" y="10" width="80" height="20" rx="2" fill="#E8A54C" opacity="0.3" stroke="#B07030" strokeWidth="1.5"/>
        <path d="M60 30 L60 10" stroke="#B07030" strokeWidth="1.5"/>
        <path d="M140 30 L140 10" stroke="#B07030" strokeWidth="1.5"/>
        <rect x="70" y="52" width="60" height="42" rx="3" fill="none" stroke="#B07030" strokeWidth="1.5" strokeDasharray="5 3"/>
        <circle cx="100" cy="73" r="12" fill="#E8A54C" opacity="0.25" stroke="#B07030" strokeWidth="1"/>
        <text x="93" y="78" fill="#B07030" fontSize="13" fontFamily="Montserrat" fontWeight="700">fb</text>
        <line x1="40" y1="115" x2="160" y2="115" stroke="#B07030" strokeWidth="1" opacity="0.3"/>
    </svg>
);

const ShelfVisual = () => (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="product-svg">
        <rect x="10" y="130" width="180" height="8" rx="2" fill="#7A5C44" opacity="0.35"/>
        <rect x="10" y="65" width="180" height="8" rx="2" fill="#7A5C44" opacity="0.35"/>
        <rect x="25" y="73" width="42" height="57" rx="3" fill="#C8883A" opacity="0.2" stroke="#B07030" strokeWidth="1.5"/>
        <rect x="79" y="73" width="42" height="57" rx="3" fill="#E8A54C" opacity="0.2" stroke="#B07030" strokeWidth="1.5"/>
        <rect x="133" y="73" width="42" height="57" rx="3" fill="#C8883A" opacity="0.2" stroke="#B07030" strokeWidth="1.5"/>
        <path d="M25 90 L67 90" stroke="#B07030" strokeWidth="1" opacity="0.5"/>
        <path d="M79 90 L121 90" stroke="#B07030" strokeWidth="1" opacity="0.5"/>
        <path d="M133 90 L175 90" stroke="#B07030" strokeWidth="1" opacity="0.5"/>
        <rect x="35" y="20" width="130" height="45" rx="4" fill="#B07030" opacity="0.12" stroke="#B07030" strokeWidth="1.5"/>
        <path d="M45 42 L55 52 L75 32" stroke="#B07030" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="85" y="48" fill="#B07030" fontSize="10" fontFamily="Montserrat" fontWeight="700" opacity="0.7">Shelf-ready</text>
    </svg>
);
